// A type-ahead multi-select: removable chips for chosen items + a search box that
// filters a dropdown of the rest. Scales to large option lists (e.g. hundreds of
// customers). Controlled — parent owns the selected id array.

import { useEffect, useMemo, useRef, useState } from "react";

export interface Option {
  id: string;
  label: string;
}

export default function MultiSelect({
  options,
  selected,
  onChange,
  placeholder = "Search…",
}: {
  options: Option[];
  selected: string[];
  onChange: (ids: string[]) => void;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  const labelById = useMemo(() => new Map(options.map((o) => [o.id, o.label])), [options]);
  const matches = useMemo(() => {
    const q = query.trim().toLowerCase();
    return options.filter((o) => !selected.includes(o.id) && (!q || o.label.toLowerCase().includes(q)));
  }, [options, selected, query]);

  // Close when clicking outside.
  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const add = (id: string) => {
    onChange([...selected, id]);
    setQuery("");
  };
  const remove = (id: string) => onChange(selected.filter((x) => x !== id));

  return (
    <div ref={ref} className="relative">
      <div className="flex flex-wrap items-center gap-1 rounded-lg border border-slate-300 px-2 py-1.5 focus-within:border-brand-500 focus-within:ring-2 focus-within:ring-brand-100">
        {selected.map((id) => (
          <span
            key={id}
            className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-xs font-medium text-brand-700"
          >
            {labelById.get(id) ?? "?"}
            <button
              type="button"
              onClick={() => remove(id)}
              className="text-brand-500 hover:text-brand-800"
              aria-label="Remove"
            >
              ✕
            </button>
          </span>
        ))}
        <input
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder={selected.length ? "" : placeholder}
          className="min-w-[7rem] flex-1 bg-transparent text-sm outline-none"
        />
      </div>
      {open && (
        <div className="absolute z-40 mt-1 max-h-48 w-full overflow-y-auto rounded-lg border border-slate-200 bg-white shadow-lg">
          {matches.length === 0 ? (
            <p className="px-3 py-2 text-sm text-slate-400">
              {query ? "No matches" : "All added"}
            </p>
          ) : (
            matches.slice(0, 100).map((o) => (
              <button
                key={o.id}
                type="button"
                onClick={() => add(o.id)}
                className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-brand-50"
              >
                {o.label}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
